<script setup>
import { computed, ref } from 'vue'
import { CAMPAIGN_CONFIG } from '../config.js'
import { useMysterySoundGuessStore } from '../stores/mysterySoundGuess'
import { useLastVoteStore } from '../stores/lastVote'
import Countdown from './Partials/Countdown.vue'
import Modal from './Partials/Modal.vue'
import { dataLayer, api, authentication } from '@dpgradio/creative'
import { storeToRefs } from 'pinia'

const { copy } = CAMPAIGN_CONFIG

defineProps({
  questionClass: {
    type: String,
    default: '',
  },
})

const mysterySoundGuess = useMysterySoundGuessStore()
const { id, guess, name, prompt, timeRemaining } = storeToRefs(mysterySoundGuess)

const lastVote = useLastVoteStore()

const START_COUNTDOWN_AT = 30

const shouldShowCountdown = computed(() => {
  return timeRemaining.value > 0 && timeRemaining.value <= START_COUNTDOWN_AT
})

const showConfirmDialog = ref(false)
const showSomethingWentWrongDialog = ref(false)

const triggerConfirmDialog = () => {
  showConfirmDialog.value = true
  dataLayer.pushCampaignAction('vote_attempt', {
    id: id.value,
    timeRemaining: timeRemaining.value,
  })
}

const closeConfirmDialog = () => {
  showConfirmDialog.value = false
  dataLayer.pushCampaignAction('vote_cancel', {
    id: id.value,
    timeRemaining: timeRemaining.value,
  })
}

const submitVote = async () => {
  await authentication.require()

  try {
    await api.request().post(`/game/mystery_sound/${prompt.value}/guesses/${id.value}/guess_vote`, {
      correct: true,
    })
  } catch (error) {
    console.error('Error submitting vote:', error)

    // The error is just a string, so we check if it contains '422' to see if the user has already voted.
    const hasAlreadyVoted = error.message.includes('422')

    if (hasAlreadyVoted) {
      // The user has already voted. We don't know for which guess, so store an empty vote.
      lastVote.storeVote(null)
      return
    }

    showSomethingWentWrongDialog.value = true
    return
  }

  lastVote.storeVote(id.value)
  dataLayer.pushCampaignAction('vote_confirm', {
    id: id.value,
    timeRemaining: timeRemaining.value,
  })
}
</script>

<template>
  <div>
    <div class="space-y-6">
      <!-- eslint-disable-next-line vue/no-v-html -->
      <div :class="questionClass" class="text-xl text-white" v-html="copy.voteQuestion(guess, name)" />

      <button
        data-testid="vote-button"
        class="w-full font-heading font-semibold bg-brand-primary hover:bg-brand-primary-dark rounded-full shadow-sm px-4 py-3 uppercase transition-colors"
        style="font-size: clamp(1rem, 5cqw, 1.5rem)"
        @click="triggerConfirmDialog"
      >
        {{ copy.voteButton }}
      </button>
    </div>

    <Transition
      enterActiveClass="transition-[max-height,opacity] duration-500"
      leaveActiveClass="transition-[max-height,opacity] duration-500"
      enterFromClass="max-h-0 opacity-0"
      enterToClass="max-h-[80px] opacity-100"
      leaveFromClass="max-h-[80px] opacity-100"
      leaveToClass="max-h-0 opacity-0"
    >
      <Countdown v-if="shouldShowCountdown" :timeRemaining="timeRemaining" :startCountdownAt="START_COUNTDOWN_AT" />
    </Transition>

    <Modal :show="showConfirmDialog" :title="copy.confirmTitle">
      <!-- eslint-disable-next-line vue/no-v-html -->
      <p class="text-lg" v-html="copy.confirmText" />
      <div class="flex justify-center space-x-4 mt-4">
        <button
          data-testid="confirm-cancel"
          class="bg-gray-300 font-heading uppercase hover:bg-gray-400 text-gray-800 font-semibold py-2 px-4 rounded"
          style="font-size: clamp(0.875rem, 5cqw, 1.25rem)"
          @click="closeConfirmDialog"
        >
          {{ copy.confirmCancel }}
        </button>
        <button
          data-testid="confirm-submit"
          class="bg-brand-primary font-heading uppercase hover:bg-brand-primary-dark text-white font-semibold py-2 px-4 rounded"
          style="font-size: clamp(0.875rem, 5cqw, 1.25rem)"
          @click="submitVote"
        >
          {{ copy.confirmSubmit }}
        </button>
      </div>
    </Modal>

    <Modal :show="showSomethingWentWrongDialog" :title="copy.errorTitle">
      <p class="text-lg">{{ copy.errorText }}</p>
      <div class="flex justify-center space-x-4 mt-4">
        <button
          data-testid="error-retry"
          class="bg-brand-primary font-heading uppercase hover:bg-brand-primary-dark text-white font-semibold py-2 px-4 rounded"
          style="font-size: clamp(0.875rem, 5cqw, 1.25rem)"
          @click="showSomethingWentWrongDialog = false"
        >
          {{ copy.errorButton }}
        </button>
      </div>
    </Modal>
  </div>
</template>
